import React, { useState } from 'react';
import { Input, Button, Space, Typography, message } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import { DEFAULT_HIGHLIGHT_STYLE, highlightText } from '../utils/highlightText';

const { TextArea } = Input;
const { Text } = Typography;

interface AudioTranscriptionEditorProps {
  audioId: string;
  deviceId: string;
  transcription?: string;
  confidence?: number;
  highlightQuery?: string;
  onUpdate: (newTranscription: string) => void;
}

const AudioTranscriptionEditor: React.FC<AudioTranscriptionEditorProps> = ({
  audioId,
  deviceId,
  transcription = '',
  confidence,
  highlightQuery,
  onUpdate
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(transcription);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { fetchAuthSession } = await import('../auth/gateway');
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      
      if (!token) return;
      
      const { API_CONFIG } = await import('../config/api');
      const response = await fetch(`${API_CONFIG.BASE_URL}/api/v1/devices/${deviceId}/audio/${audioId}/transcription`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ transcription: editValue }),
      });
      
      if (response.ok) {
        onUpdate(editValue);
        setIsEditing(false);
        message.success('音声認識結果を更新しました');
      } else {
        message.error('更新に失敗しました');
      }
    } catch (error) {
      message.error('通信エラーが発生しました');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditValue(transcription);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div>
        <TextArea
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          rows={3}
          placeholder="音声認識結果を編集..."
        />
        <Space style={{ marginTop: 8 }}>
          <Button 
            type="primary" 
            size="small" 
            icon={<SaveOutlined />}
            loading={saving}
            onClick={handleSave}
          >
            保存
          </Button>
          <Button 
            size="small" 
            icon={<CloseOutlined />}
            onClick={handleCancel}
          >
            キャンセル
          </Button>
        </Space>
      </div>
    );
  }

  return (
    <div>
      {transcription ? (
        <div>
          <Text>
            "{highlightText(transcription, highlightQuery ?? '', DEFAULT_HIGHLIGHT_STYLE)}"
          </Text>
          {confidence && (
            <Text type="secondary" style={{ marginLeft: 8 }}>
              (信頼度: {(confidence * 100).toFixed(1)}%)
            </Text>
          )}
        </div>
      ) : (
        <Text type="secondary">音声認識結果なし</Text>
      )}
      <Button 
        type="link" 
        size="small" 
        icon={<EditOutlined />}
        onClick={() => setIsEditing(true)}
      >
        編集
      </Button>
    </div>
  );
};

export default AudioTranscriptionEditor;
